import './config/rum'

import { SSOProvider } from '@bits-app/react-sso'
import { StrictMode, Suspense, lazy } from 'react'
import CacheBuster from 'react-cache-buster'
import ReactDOM from 'react-dom'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'

import Loader from '@/components/ui/Loader/Loader'
import { isProdEnv } from '@/config/config'
import { APP_VERSION } from '@/config/constants'

import ErrorNetworkLoadingPage from './components/Error/ErrorNetworkLoadingPage/ErrorNetworkLoadingPage'
import { UserProvider } from './contexts/user/User.context'
import { AuthenticationProvider } from './providers/authentication/Authentication.context'
import { ConversationsProvider } from './providers/Conversations.provider'
import { FirebaseProvider } from './providers/firebase/Firebase.provider'
import { LayoutProvider } from './providers/Layout.provider'
import { NetworkGateawayProvider } from './providers/NetworkGateway.provider'
import { SellerTierProvider } from './providers/SellerTier.provider'
import { UserEventProvider } from './providers/UserEvent.provider'
import { store, persistor } from './reducers'

const Router = lazy(() => import('./views/Router/Router'))

import 'dayjs/locale/fr'
import 'dayjs/locale/de'
import 'dayjs/locale/en'

import './translations/i18n'
import './index.less'

import './index.scss'

const {
  VITE_APP_APPLE_SSO_CLIENT_ID_WEB,
  VITE_APP_APPLE_SSO_REDIRECT_URI,
  VITE_APP_GOOGLE_SSO_CLIENT_ID_WEB,
  VITE_APP_FACEBOOK_SSO_APP_ID,
} = import.meta.env

ReactDOM.render(
  <CacheBuster
    currentVersion={APP_VERSION}
    isEnabled={isProdEnv}
    isVerboseMode={!isProdEnv}
    loadingComponent={<Loader />}
    metaFileDirectory={'./'}
  >
    <StrictMode>
      <FirebaseProvider>
        <NetworkGateawayProvider>
          <SSOProvider
            config={{
              facebook: { appId: VITE_APP_FACEBOOK_SSO_APP_ID! },
              apple: {
                clientId: VITE_APP_APPLE_SSO_CLIENT_ID_WEB!,
                redirectUrl: VITE_APP_APPLE_SSO_REDIRECT_URI!,
              },
              google: {
                clientId: VITE_APP_GOOGLE_SSO_CLIENT_ID_WEB!,
              },
            }}
          >
            <PersistGate loading={null} persistor={persistor}>
              <Provider store={store}>
                <Suspense fallback={<ErrorNetworkLoadingPage />}>
                  <AuthenticationProvider>
                    <UserProvider>
                      <LayoutProvider>
                        <SellerTierProvider>
                          <UserEventProvider>
                            <ConversationsProvider>
                              <Suspense fallback={<ErrorNetworkLoadingPage />}>
                                <Router />
                              </Suspense>
                            </ConversationsProvider>
                          </UserEventProvider>
                        </SellerTierProvider>
                      </LayoutProvider>
                    </UserProvider>
                  </AuthenticationProvider>
                </Suspense>
              </Provider>
            </PersistGate>
          </SSOProvider>
        </NetworkGateawayProvider>
      </FirebaseProvider>
    </StrictMode>
  </CacheBuster>,
  document.getElementById('root')
)
